import React from 'react';
import { useLocale } from '../context/LocaleContext';
import { LOGO_URL } from './Logo';

// Inline SVG icons that mirror standard laundry symbols (matches the branded card design).
// Kept as pure SVG so they render crisply at any size and don't depend on any icon font.
const Ic = {
  ColdWash: () => (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 20 L56 20 L48 54 L16 54 Z" />
      <text x="32" y="42" textAnchor="middle" fontSize="14" fontWeight="700" fill="currentColor" stroke="none">30</text>
      <text x="42" y="34" textAnchor="middle" fontSize="8" fontWeight="700" fill="currentColor" stroke="none">°C</text>
    </svg>
  ),
  NoBleach: () => (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M32 10 L58 54 L6 54 Z" />
      <path d="M14 20 L50 48" />
      <path d="M50 20 L14 48" />
    </svg>
  ),
  NoTumbleDry: () => (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="12" width="48" height="40" rx="2" />
      <circle cx="32" cy="32" r="14" />
      <circle cx="32" cy="32" r="3" fill="currentColor" />
    </svg>
  ),
  Iron: () => (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 46 L58 46 L52 22 C50 18 46 16 42 16 L18 16 C14 16 10 18 8 22 Z" />
      <circle cx="32" cy="36" r="2.4" fill="currentColor" />
      <path d="M6 52 L58 52" />
    </svg>
  ),
  Washer: () => (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="8" width="48" height="48" rx="2" />
      <circle cx="32" cy="36" r="12" />
      <circle cx="16" cy="18" r="1.6" fill="currentColor" />
      <circle cx="24" cy="18" r="1.6" fill="currentColor" />
      <circle cx="48" cy="18" r="1.6" fill="currentColor" />
    </svg>
  ),
  NoIronPrint: () => (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 46 L58 46 L52 22 C50 18 46 16 42 16 L18 16 C14 16 10 18 8 22 Z" />
      <path d="M10 10 L54 58" />
      <path d="M6 52 L58 52" />
    </svg>
  ),
  Custom: () => (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="32" cy="32" r="24" />
      <path d="M32 20 L32 34" />
      <circle cx="32" cy="42" r="1.5" fill="currentColor" />
    </svg>
  ),
};

// Map of icon keys → SVG components. Used by both defaults and admin overrides.
export const CARE_ICONS = {
  coldWash: Ic.ColdWash,
  noBleach: Ic.NoBleach,
  noTumble: Ic.NoTumbleDry,
  ironMedium: Ic.Iron,
  coldMachine: Ic.Washer,
  noIronPrint: Ic.NoIronPrint,
  custom: Ic.Custom,
};

export const DEFAULT_CARE_ITEMS = [
  { iconKey: 'coldWash', tKey: 'care_coldWash' },
  { iconKey: 'noBleach', tKey: 'care_noBleach' },
  { iconKey: 'noTumble', tKey: 'care_noTumble' },
  { iconKey: 'ironMedium', tKey: 'care_ironMedium' },
  { iconKey: 'coldMachine', tKey: 'care_coldMachine' },
  { iconKey: 'noIronPrint', tKey: 'care_noIronPrint' },
];

// Accepts optional `items` — an array of { iconKey, label } objects (admin override).
// When falsy/empty, renders the six localized defaults.
const CareInstructions = ({ items }) => {
  const { t } = useLocale();
  const list = Array.isArray(items) && items.length > 0
    ? items.map((it) => ({ iconKey: it.iconKey || 'custom', label: it.label || '' }))
    : DEFAULT_CARE_ITEMS.map((d) => ({ iconKey: d.iconKey, label: t(d.tKey) }));

  return (
    <section
      className="mt-6 border border-gray-200 rounded-xl overflow-hidden bg-white"
      data-testid="care-instructions"
      aria-labelledby="care-instructions-heading"
    >
      {/* Header row — brand strip on the left, welcome copy on the right */}
      <div className="grid md:grid-cols-[180px_1fr] bg-gradient-to-br from-black to-neutral-900 text-white">
        <div className="flex items-center justify-center p-5 border-b md:border-b-0 md:border-r border-white/10">
          <div className="text-center">
            <img
              src={LOGO_URL}
              alt="FERON"
              className="h-20 w-auto object-contain mx-auto drop-shadow-[0_0_18px_rgba(34,197,94,0.55)]"
              draggable="false"
              loading="lazy"
              data-testid="care-wolf-logo"
            />
            <div className="text-[9px] tracking-[0.25em] mt-2 text-white/60">{t('careByLine')}</div>
          </div>
        </div>
        <div className="p-5">
          <p className="text-[13px] leading-relaxed text-white/90">{t('careThankYou')}</p>
        </div>
      </div>

      {/* Care instructions title */}
      <div className="px-5 pt-5 pb-1 text-center">
        <h3 id="care-instructions-heading" className="text-xl md:text-2xl font-black tracking-tight">{t('careInstructions')}</h3>
        <p className="text-[11px] md:text-xs text-gray-500 mt-1">{t('careTagline')}</p>
      </div>

      {/* Icons grid — 3 across on mobile, up to 6 across on wide screens */}
      <div className="grid grid-cols-3 gap-2 md:gap-3 p-5">
        {list.map((it, i) => {
          const Icon = CARE_ICONS[it.iconKey] || CARE_ICONS.custom;
          return (
            <div
              key={`${it.iconKey}-${i}`}
              className="bg-neutral-50 rounded-lg p-2.5 md:p-3 text-center flex flex-col items-center gap-1.5 hover:bg-neutral-100 transition"
              data-testid={`care-item-${it.iconKey}-${i}`}
            >
              <div className="w-11 h-11 text-gray-800">
                <Icon />
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-700 leading-tight">
                {it.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer strip */}
      <div className="border-t bg-neutral-50 px-5 py-2.5 flex items-center justify-between flex-wrap gap-2">
        <span className="text-[11px] font-bold text-gray-700">{t('careFooterLeft')}</span>
        <a href="https://www.feronstore.com" target="_blank" rel="noreferrer noopener" className="text-[11px] font-bold text-[#22c55e] hover:underline">
          www.feronstore.com
        </a>
      </div>
    </section>
  );
};

export default CareInstructions;
